import { differenceInDays } from 'date-fns';
import { useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import BlankImage from '../../../../assets/images/blank-image.png';
import { Icon } from '../../../../components/common/icon/Icon';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { PriceSuggestModal } from '../../../../components/common/modal/PriceSuggestModal';
import { CallGuideModal } from '../../../../components/custom/modal/popup/CallGuideModal';
import { flarelaneEventKeys } from '../../../../constants/flarelaneEvent/flarelaneEvent.contant';
import {
  OpenMarketProductApporovalStatusType,
  OpenMarketProductApprovalStatusConstant,
  OpenMarketProductStatusConstant,
  OpenMarketProductStatusConstantType,
} from '../../../../constants/openMarketProduct/openMarketProduct';
import { usePostUserSellTractorInquiries } from '../../../../hooks/api/inquiry/mutations/usePostUserSellTractorInquiries';
import { useVirtualNumberInquiry } from '../../../../hooks/product/useVirtualNumberInquiry';
import { useAppSelector } from '../../../../hooks/store/useStore';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { IOpenMarketProduct } from '../../../../interfaces/OpenMarketProduct.interfaces';
import { formatPublishHistory } from '../../../../utils/formatter/formatPublishHistory.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { getOpenMarketDetailPageURL } from '../../../../utils/getter/getProductDetailPageURL.util';
import { getOpenMarketProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { getRegionByDepth, RegionDepth } from '../../../../utils/getter/getShortAddress.util';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { ProductChip } from '../components/ProductChip';

export type IOpenMarketProductForDetailCard = Pick<
  IOpenMarketProduct,
  | 'id'
  | 'updatedAt'
  | 'views'
  | 'inquiryCount'
  | 'images'
  | 'machine'
  | 'manufacturedYear'
  | 'location'
  | 'operationTime'
  | 'approvalStatus'
  | 'status'
  | 'approvalDate'
  | 'hopeSellingPrice'
  | 'pokeCount'
  | 'code'
  | 'bump'
  | 'isNewProduct'
  | 'user'
  | 'priceNegotiationYn'
>;

interface OpenMarketProductDetailCardProps {
  product: IOpenMarketProductForDetailCard;
  isNewBadgeVisible?: boolean;
  indexInList: number;
  searchKeyword?: string;
}

const LIKE_NEW_OPERATION_HOURS = 200;

export const OpenMarketProductDetailCard = ({
  product,
  isNewBadgeVisible = false,
  indexInList,
  searchKeyword,
}: OpenMarketProductDetailCardProps) => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();
  const [isShowPriceSuggestModal, setIsShowPriceSuggestModal] = useState(false);
  const [isShowPriceNegotiationUnavailableModal, setIsShowPriceNegotiationUnavailableModal] = useState(false);
  const [isShowMyTractorModal, setIsShowMyTractorModal] = useState(false);

  const { APPROVAL, ONGOING, REJECT } = OpenMarketProductApprovalStatusConstant;
  const { ONSALE, SOLDOUT } = OpenMarketProductStatusConstant;

  const directDealFilter = useAppSelector((state) => state.filter.directDealFilter);

  const { mutate: postUserSellTractorInquiries } = usePostUserSellTractorInquiries();

  const { callWithVirtualNumber, shouldShowModal, virtualNumber, closeModal } = useVirtualNumberInquiry({
    productId: product?.id || '',
    productCode: product?.code || '',
    sellerPhoneNumber: product?.user?.phoneNumber || '',
  });

  const handleClick = () => {
    if (product.approvalStatus === APPROVAL && product.status === ONSALE) {
      const url = getOpenMarketDetailPageURL(product.id, product.machine.machineType);
      navigate(url);
      // 모든 필터에 대해 활성화된 필터가 있는지 확인
      const hasAnyActiveFilter = directDealFilter.globalFilters.some((filter) => {
        let isFilterButtonActive = false;

        if (filter.optionType === 'radio' || filter.optionType === 'checkbox') {
          filter.options.forEach((option) => {
            if (option.isSelected && !option.isDefault) {
              isFilterButtonActive = true;
            }
          });
        } else if (filter.optionType === 'slider') {
          filter.options.forEach((option) => {
            if (option.value !== option.defaultValue) {
              isFilterButtonActive = true;
            }
          });
        }

        return isFilterButtonActive;
      });

      // 검색 결과 페이지에서 카드 클릭 시 이벤트 전송
      if (searchKeyword) {
        ampli.pvmClickOpenMarketSearchResultCard({
          typed_keyword: searchKeyword,
          code: product.code,
          product_id: product.id,
          product_title: title,
          price: product.hopeSellingPrice,
        });
      } else {
        // 검색 결과 페이지가 아닐 때만 OLP 카드 클릭 이벤트 전송
        ampli.pvmClickOpenMarketDetailCardOnPlp({
          index: indexInList.toString(),
          is_any_filter_selected: hasAnyActiveFilter,
          filters: [...directDealFilter.globalFilters],
          title: `${title}, ${product.manufacturedYear.toString()}년식, ${product.operationTime.toString()}시간`,
          code: product.code,
          bump_times: product.bump?.bumpTimes || 0,
          days_since_last_bump: product.bump?.lastBumpAt
            ? differenceInDays(new Date(), new Date(product.bump.lastBumpAt))
            : 0,
          is_bumped_before: !!product.bump,
          is_new_product: product.isNewProduct,
          is_mine: isMyProduct,
        });
      }
    }
  };

  const getBlurEffect = (
    status: OpenMarketProductStatusConstantType,
    approvalStatus: OpenMarketProductApporovalStatusType,
  ) => {
    const BLURRED = true;
    const NOT_BLURRED = false;
    const DIMMED_TEXT = {
      SOLDOUT: '판매 완료',
      ONGOING: '심사중',
      REJECT: '판매불가',
      DEFAULT: '',
    };

    switch (approvalStatus) {
      case APPROVAL:
        return {
          isBlurred: status === SOLDOUT ? BLURRED : NOT_BLURRED,
          dimmedText: status === SOLDOUT ? DIMMED_TEXT.SOLDOUT : DIMMED_TEXT.DEFAULT,
        };

      case ONGOING:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.ONGOING,
        };

      case REJECT:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.REJECT,
        };

      default:
        return {
          isBlurred: NOT_BLURRED,
          dimmedText: DIMMED_TEXT.DEFAULT,
        };
    }
  };
  const blurEffect = getBlurEffect(product.status, product.approvalStatus);
  const { isBlurred, dimmedText } = blurEffect;

  const isNewOpenMarketProduct = product.isNewProduct;
  // 비로그인이거나 판매자 정보가 없으면 둘 다 undefined라 같다고 판단되므로, 내 id가 있을 때만 비교한다.
  const isMyProduct = !!userProfile?.id && product.user?.id === userProfile.id;

  const title = getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine });
  const brandName = product.machine?.brand?.name || '';
  const modelName = product.machine?.model || '';

  const price = useMemo<string>(() => {
    return getOpenMarketProductPrice(product.status, product.hopeSellingPrice);
  }, [product]);

  const handlePokeSuggestButtonClick = () => {
    ampli.pvmClickOdpSuggestPriceButton({ brand: brandName, model: modelName, title });

    // 가격 협상 가능 여부 체크
    if (isMyProduct) {
      setIsShowMyTractorModal(true);
      return;
    }

    if (!product.priceNegotiationYn) {
      setIsShowPriceNegotiationUnavailableModal(true);
      return;
    }

    setIsShowPriceSuggestModal(true);
  };

  const handleInquiryButtonClick = () => {
    if (!product) {
      return;
    }

    if (isMyProduct) {
      setIsShowMyTractorModal(true);
      return;
    }

    ampli.마켓오픈마켓매물전화상담버튼클릭({
      '게시글 제목':
        getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }) ?? '',
      '매물 코드': product?.code,
    });

    postUserSellTractorInquiries({ reqBody: { userSellTractorId: product.id } });

    ampli.pvmProductInquiry({
      title: getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }) ?? '',
      type: 'MemberToMember',
      page_type: 'olp',
      ps: product.machine.engineOutputInPs,
      manufacturedYear: product.manufacturedYear,
      operationTime: product.operationTime,
    });
    ReactGA.event('pvm_product_inquiry', {
      category: 'pvm',
      action: 'pvm_product_inquiry',
      value: 'MemberToMember',
    });

    if (window.ReactNativeWebView) {
      postMessageToRN({
        messageKey: 'flarelane_trackEvent',
        messageParam: flarelaneEventKeys.pvm_inquiry_user_sell_product,
        messageData: {
          productId: product.id,
          brandName: product.machine.brand?.name,
          machineModel: product.machine?.model,
        },
      });
    }

    // 안심번호로 전화 걸기
    callWithVirtualNumber();
  };

  const productImageUrl = useMemo(() => {
    if (!product.images) {
      return BlankImage;
    }

    if (product.images.length === 0) {
      return BlankImage;
    }

    if (product.images.find((image) => image.type === 'LEFT')) {
      return product.images.find((image) => image.type === 'LEFT')?.imageUrl;
    }

    return product.images[0]?.imageUrl;
  }, [product.images]);

  return (
    <Container onClick={handleClick}>
      <ImageWrap>
        <Image src={productImageUrl} alt={productImageUrl} isBlurred={isBlurred} loading={'lazy'} />
        {isBlurred && <DimmedText>{dimmedText}</DimmedText>}
        {isNewBadgeVisible && isNewOpenMarketProduct && (
          <BadgeContainer>
            <ProductChip type={'NEW'} />
          </BadgeContainer>
        )}
        {isMyProduct && (
          <MyProductBadge>
            <MyProductBadgeText>내 매물</MyProductBadgeText>
          </MyProductBadge>
        )}
      </ImageWrap>

      <ProductInfoSection isBlurred={isBlurred}>
        <Title>{title}</Title>
        <TopContainer>
          <Price>{price}</Price>
          <TractorSummaryText>{'·'}</TractorSummaryText>
          <TractorSummaryText>{`${String(product.manufacturedYear).slice(2, 4)}년식`}</TractorSummaryText>
          <TractorSummaryText>{'·'}</TractorSummaryText>
          <TractorSummaryText>{`${Number(product.operationTime).toLocaleString()}시간`}</TractorSummaryText>
        </TopContainer>
        <BottomContainer>
          <ProductMetaContainer>
            <SmallTextContainer>
              <SmallText>{getRegionByDepth(product.location, RegionDepth.REGION)}</SmallText>
              <SmallText>{'·'}</SmallText>
              <SmallText
                color={
                  !isNewOpenMarketProduct &&
                  product.bump?.lastBumpAt &&
                  differenceInDays(new Date(), new Date(product.bump.lastBumpAt)) <= 7
                    ? '#FF3049'
                    : 'var(--Light-Scheme-Monochrome-offblack, #505363)'
                }
              >
                {formatPublishHistory({
                  approvalDate: product.approvalDate,
                  updatedAt: product.updatedAt,
                  lastBumpAt: !isNewOpenMarketProduct ? product.bump?.lastBumpAt : null,
                })}
              </SmallText>
            </SmallTextContainer>

            <MetricsGroup>
              <InquiryCount>
                <Icon name={'Envelope'} size={'Medium'} fill={'#C6C6D5'} />
                <Count>{Number(product.pokeCount || 0).toLocaleString()}</Count>
              </InquiryCount>
              <SplitBarWrapper>
                <SplitBar>{'|'}</SplitBar>
              </SplitBarWrapper>
              <InquiryCount>
                <Icon name={'Phone'} size={'Medium'} fill={'#C6C6D5'} />
                <Count>{Number(product.inquiryCount || 0).toLocaleString()}</Count>
              </InquiryCount>
            </MetricsGroup>
          </ProductMetaContainer>
          {isMyProduct ? (
            <ButtonContainer>
              <ManageButton>{'관리하기'}</ManageButton>
            </ButtonContainer>
          ) : (
            <ButtonContainer>
              <LeftButton
                color={!product.priceNegotiationYn ? 'var(--Light-Scheme-Monochrome-black, #C6C6D5)' : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePokeSuggestButtonClick();
                }}
              >
                {'문자하기'}
              </LeftButton>
              <RightButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleInquiryButtonClick();
                }}
              >
                {'전화하기'}
              </RightButton>
            </ButtonContainer>
          )}
        </BottomContainer>
      </ProductInfoSection>
      <PriceSuggestModal
        isShow={isShowPriceSuggestModal}
        onClose={() => setIsShowPriceSuggestModal(false)}
        productId={product.id}
        productCode={product.code}
        hopeSellingPrice={product.hopeSellingPrice}
        productTitle={title}
        brandName={brandName}
        modelName={modelName}
        phoneNumber={product.user?.phoneNumber || ''}
        machineType={product.machine.machineType}
      />
      <PopupConfirmModal
        isShow={isShowMyTractorModal}
        hasCloseButton={false}
        closeModal={() => setIsShowMyTractorModal(false)}
        title={'내가 판매중인 매물이예요 🙂'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsShowMyTractorModal(false),
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isShowPriceNegotiationUnavailableModal}
        hasCloseButton={false}
        closeModal={() => setIsShowPriceNegotiationUnavailableModal(false)}
        title={'가격 협상이 불가능해요 😓'}
        description={'판매자 분께서 가격 제안을 원치 않아요.\n혹시 모르니 전화로 문의 해보세요!'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setIsShowPriceNegotiationUnavailableModal(false),
            color: 'blue',
          },
        ]}
      />
      <CallGuideModal isShow={shouldShowModal} virtualNumber={virtualNumber} onClose={closeModal} />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 160px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  align-self: stretch;

  gap: 16px;

  cursor: pointer;
`;

const ImageWrap = styled.div`
  min-width: 150px;
  height: 100%;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;

  padding: 0px 16px;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.4 : 1)};
`;

const BadgeContainer = styled.div`
  position: absolute;

  top: 8px;
  left: 8px;

  z-index: 50;

  display: flex;
  gap: 4px;
`;

const MyProductBadge = styled.div`
  display: flex;
  height: 28px;
  padding: 0 13px 0 12px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  position: absolute;
  right: 0;
  bottom: 0;

  border-radius: 8px 0 8px 0;
  background: var(--Light-Scheme-Color-primary-red, #ff3049);
`;

const MyProductBadgeText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const DimmedText = styled.span`
  position: absolute;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 20.4px */

  mix-blend-mode: darken;

  opacity: 0.4;

  white-space: nowrap;
`;

const ProductInfoSection = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  padding-top: 4px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductContentGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const TopContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  overflow: hidden;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const TractorSummaryText = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const ProductMetaContainer = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  align-self: stretch;
  flex-wrap: wrap;
`;

const SmallTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  flex: 1 0 0;
`;

const SmallText = styled.span<{ color?: string }>`
  color: ${(props) => props.color || 'var(--Light-Scheme-Monochrome-offblack, #505363)'};
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const MetricsGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: stretch;

  @media screen and (max-width: 340px) {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  height: 28px;
  align-items: center;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const ViewCount = styled.span`
  display: flex;
  padding: 2px 0px;
  align-items: center;
`;

const InquiryCount = styled.span`
  display: flex;
  padding: 2px 0px;
  align-items: center;
`;

const Count = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const SplitBarWrapper = styled.div`
  display: flex;
  padding: 0px 2px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const SplitBar = styled.span`
  color: var(--Light-Scheme-Monochrome-grey20, #ececf3);
  text-align: center;

  /* System/Medium/m-light */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 85%; /* 11.9px */
`;

const Button = styled.button<{ color?: string }>`
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  align-self: stretch;

  color: ${(props) => props.color || 'var(--Light-Scheme-Monochrome-offblack, #505363)'};

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const LeftButton = styled(Button)`
  border-right: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const RightButton = styled(Button)``;

const ManageButton = styled(Button)``;
